import { IconCheckbox, IconChevronRight, IconLock } from '@tabler/icons-react-native';
import { presentInsuredDetailsAtom } from 'app/atoms';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { InsuredBadge } from 'app/design/icons/insured-badge';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Modal } from 'app/design/primitives/modal';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';
import { localeAwareFormat } from 'app/lib/date';
import { DEFAULT_PLAN_CODE, isFreeTrialPlanCode } from 'app/network/plan';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Plan } from 'app/types/plan';
import type { CurrencyType } from 'app/schemas/user';
import { isLoanDiscountActive } from 'app/utils/loan-discount';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { GroupSubtypeEnum, PlanRecurrenceEnum } from 'app/schemas/plan';
import { useSetAtom } from 'jotai';
import { useLoanDiscount } from 'app/hooks/use-loan-discount';
import { useUISize } from 'app/utils/web-helpers';

const theme = createDSTheme();

interface MyPlanRedesignProps {
  activePlan?: Plan;
  hasExpiredPlan: boolean;
  isActivationModalOpen: boolean;
  isStopRecurrenceModalOpen: boolean;
  isStoppingRecurrence: boolean;
  onActivate: () => void;
  onCloseStopRecurrenceModal: () => void;
  onConfirmStopRecurrence: () => void;
  onOpenStopRecurrenceModal: () => void;
  onSubscribe: () => void;
  renderActivationModal: React.ReactNode;
  showActivationButton: boolean;
  userCurrency: CurrencyType;
}

function CheckFeature({
  children,
  description,
  icon,
  status,
}: {
  children: React.ReactNode;
  description?: string;
  icon?: React.ReactNode;
  status?: React.ReactNode;
}) {
  return (
    <Row alignItems="flex-start" gap="spacing.general.x4">
      {icon ?? <IconCheckbox color={theme.colors['icon.primary']} size={30} strokeWidth={ICON_STROKE_WIDTH} />}
      <Column flex={1} gap="spacing.general.x1" paddingTop="spacing.general.x1">
        <Text color="text.primary" fontSize={16} fontWeight="700" lineHeight={20}>
          {children}
        </Text>
        {description ? (
          <Text color="text.primary" fontSize={12} lineHeight={16}>
            {description}
          </Text>
        ) : null}
        {status}
      </Column>
    </Row>
  );
}

function FeatureDivider() {
  return <Separator color={theme.colors['border.divider']} />;
}

function DamageCoverageFeature({ onPress, showLearnMore = false }: { onPress?: () => void; showLearnMore?: boolean }) {
  const { t } = useTranslation('my-plan');
  return (
    <Pressable disabled={!onPress} onPress={onPress}>
      <Row
        alignItems="center"
        backgroundColor="surface.secondary"
        borderRadius="radius.md"
        gap="spacing.general.x2"
        padding="spacing.general.x2"
      >
        <InsuredBadge
          checkColor={theme.colors['icon.brand']}
          color={theme.colors['surface.default']}
          height={30}
          width={30}
        />
        <Column flex={1} gap="spacing.general.x1">
          <Text color="text.primary" fontSize={16} fontWeight="700">
            {t('design.damageCoverage')}
          </Text>
          {showLearnMore ? (
            <Text color="text.primary" fontSize={14} textDecorationLine="underline">
              {t('design.learnMore')}
            </Text>
          ) : null}
        </Column>
        <IconChevronRight color={theme.colors['icon.primary']} size={24} strokeWidth={2} />
      </Row>
    </Pressable>
  );
}

function Benefits({ onShowInsurance }: { onShowInsurance: () => void }) {
  const { t } = useTranslation('my-plan');
  return (
    <Column gap="spacing.general.x6">
      <Text color="text.primary" variant="bodyLargeStrong">
        {t('design.yourBenefits')}
      </Text>
      <CheckFeature>{t('design.unlimitedLending')}</CheckFeature>
      <CheckFeature>{t('design.unlimitedBorrowing')}</CheckFeature>
      <DamageCoverageFeature onPress={onShowInsurance} />
    </Column>
  );
}

function PlanActionButton({ children, onPress }: { children: React.ReactNode; onPress: () => void }) {
  return (
    <Button
      minHeight={56}
      onPress={onPress}
      style={{
        borderColor: theme.colors['component.button.border.tertiary'],
        borderRadius: theme.borderRadii['radius.full'],
        borderWidth: theme.borderWidths['border-width.standard'],
      }}
      variant="plain"
    >
      <Text color="text.primary" textAlign="center" variant="bodyBaseMedium">
        {children}
      </Text>
    </Button>
  );
}

export function MyPlanRedesign({
  activePlan,
  hasExpiredPlan,
  isActivationModalOpen,
  isStopRecurrenceModalOpen,
  isStoppingRecurrence,
  onActivate,
  onCloseStopRecurrenceModal,
  onConfirmStopRecurrence,
  onOpenStopRecurrenceModal,
  onSubscribe,
  renderActivationModal,
  showActivationButton,
  userCurrency,
}: MyPlanRedesignProps) {
  const { t } = useTranslation(['my-plan', 'payment', 'common']);
  const presentInsuredDetails = useSetAtom(presentInsuredDetailsAtom);
  const { creditUsedAt, discountDeadline, freeBorrowIsUsed, isReady: isLoanDiscountReady } = useLoanDiscount();
  const hasActiveLoanDiscount = isLoanDiscountActive(discountDeadline);
  const hasSubscription = Boolean(activePlan && !hasExpiredPlan && !isFreeTrialPlanCode(activePlan.code.code));
  const { isLargeUI } = useUISize();
  const isRecurring =
    hasSubscription &&
    (activePlan?.recurrence === PlanRecurrenceEnum.yearly || activePlan?.recurrence === PlanRecurrenceEnum.monthly);
  const isCancelledSubscription =
    hasSubscription &&
    !activePlan?.code.group &&
    activePlan?.code.code === DEFAULT_PLAN_CODE &&
    activePlan.recurrence === PlanRecurrenceEnum.no;
  const isSponsoredSubscription = hasSubscription && Boolean(activePlan?.code.group);
  const showInsuranceDetails = () => {
    presentInsuredDetails({ insured: true });
  };

  const horizontalPadding = theme.spacing[hasSubscription ? 'spacing.general.x4' : 'spacing.general.x2'];
  const verticalPadding = theme.spacing['spacing.general.x6'];

  return (
    <>
      <Page
        paddingStyle={{
          paddingBottom: verticalPadding,
          paddingLeft: horizontalPadding,
          paddingRight: horizontalPadding,
          paddingTop: verticalPadding,
        }}
      >
        <Column
          alignSelf="center"
          gap="spacing.general.x6"
          maxWidth={isLargeUI ? 640 : hasSubscription ? undefined : 370}
          width="100%"
        >
          {hasSubscription && activePlan ? (
            <Column gap="spacing.general.x6">
              <Column borderColor="border.secondary" borderRadius="radius.lg" borderWidth={1} overflow="hidden">
                <Row
                  alignItems="center"
                  backgroundColor="surface.brand.secondary-5.subtle"
                  justifyContent="space-between"
                  padding="spacing.general.x6"
                >
                  <PartagePlusBadge />
                  <Text color="text.secondary" variant="bodyBase">
                    {activePlan.code.group
                      ? t(
                          `design.planType.${
                            activePlan.code.group.subtype === GroupSubtypeEnum.enterprise ? 'employee' : 'citizen'
                          }`,
                        )
                      : t(
                          `design.frequency.${
                            activePlan.recurrence === PlanRecurrenceEnum.no
                              ? new Date(activePlan.expiresAt ?? 0).getTime() -
                                  new Date(activePlan.createdAt).getTime() >
                                60 * 24 * 60 * 60 * 1000
                                ? PlanRecurrenceEnum.yearly
                                : PlanRecurrenceEnum.monthly
                              : activePlan.recurrence
                          }`,
                        )}
                  </Text>
                </Row>
                <Column gap="spacing.general.x4" padding="spacing.general.x6">
                  {isSponsoredSubscription && activePlan.code.group ? (
                    <Column alignItems="center" gap="spacing.general.x2">
                      <Text color="text.primary" variant="bodyBase">
                        {t('design.offeredBy')}
                      </Text>
                      <PCImage
                        contentFit="contain"
                        height={64}
                        source={
                          activePlan.code.group.picture ? { uri: activePlan.code.group.picture } : images.logoThreeLines
                        }
                        style={{
                          backgroundColor: '#fff',
                          borderColor: theme.colors['border.secondary'],
                          borderRadius: theme.borderRadii['radius.full'],
                          borderWidth: theme.borderWidths['border-width.subtle'],
                        }}
                        width={64}
                      />
                      <Text color="text.primary" textAlign="center" variant="bodyXLargeStrong">
                        {activePlan.code.group.name}
                      </Text>
                    </Column>
                  ) : isCancelledSubscription ? (
                    <Text color="text.primary" variant="bodyBase">
                      {t('design.renewalCancelled')}
                    </Text>
                  ) : (
                    <Column gap="spacing.general.x2">
                      {isRecurring ? (
                        <Text color="text.primary" variant="bodyLargeMedium">
                          {t(`price.${activePlan.recurrence}.${userCurrency}`, { ns: 'payment' })}
                          {userCurrency === 'CAD' ? ' CAD' : ''}/
                          {t(`period.${activePlan.recurrence}`, { ns: 'payment' })}*
                        </Text>
                      ) : (
                        <Text color="text.primary" variant="bodyBase">
                          {t('design.activeSubscription')}
                        </Text>
                      )}
                      {isRecurring ? (
                        <Text color="text.secondary" variant="bodyXSmall">
                          {t(`design.billingNote.${activePlan.recurrence}`, {
                            price: `${t(`price.${activePlan.recurrence}.${userCurrency}`, { ns: 'payment' })}${
                              userCurrency === 'CAD' ? ' CAD' : ''
                            }`,
                          })}
                        </Text>
                      ) : null}
                    </Column>
                  )}
                  <FeatureDivider />
                  <Column gap="spacing.general.x2">
                    <Text color="text.primary" variant="bodyBase">
                      {t(isRecurring ? 'design.nextPayment' : 'design.subscriptionEnd')}
                    </Text>
                    <Text color="text.primary" variant="bodyBaseStrong">
                      {activePlan.expiresAt
                        ? localeAwareFormat(new Date(activePlan.expiresAt), t('dateFormat_MMMMdYYYY', { ns: 'common' }))
                        : t('noEnd')}
                    </Text>
                  </Column>
                  {isRecurring ? (
                    <PlanActionButton onPress={onOpenStopRecurrenceModal}>
                      {t('stopRecurrenceButtonText')}
                    </PlanActionButton>
                  ) : isCancelledSubscription ? (
                    <PlanActionButton onPress={onSubscribe}>{t('design.resubscribe')}</PlanActionButton>
                  ) : null}
                </Column>
              </Column>

              {isSponsoredSubscription && showActivationButton ? (
                <PlanActionButton onPress={onActivate}>{t('design.useActivationCode')}</PlanActionButton>
              ) : null}

              <Benefits onShowInsurance={showInsuranceDetails} />
            </Column>
          ) : (
            <Column gap="spacing.general.x4">
              <Column borderColor="border.secondary" borderRadius="radius.lg" borderWidth={1} overflow="hidden">
                <Row
                  alignItems="center"
                  backgroundColor="surface.info.subtle"
                  gap="spacing.general.x2"
                  padding="spacing.general.x6"
                >
                  <Text color="text.primary" fontSize={20} fontWeight="700" lineHeight={22}>
                    {t('design.discovery')}
                  </Text>
                  <Row
                    backgroundColor="surface.primary-neutral"
                    borderRadius="radius.sm"
                    paddingHorizontal="spacing.general.x2"
                    paddingVertical="spacing.general.x1"
                  >
                    <Text color="text.secondary" fontSize={12} fontWeight="700" lineHeight={18}>
                      {t('design.current')}
                    </Text>
                  </Row>
                </Row>
                <Column gap="spacing.general.x4" padding="spacing.general.x6">
                  <CheckFeature description={t('design.unlimitedLendingDescription')}>
                    {t('design.unlimitedLending')}
                  </CheckFeature>
                  <CheckFeature
                    description={t('design.freeBorrowDescription')}
                    icon={
                      freeBorrowIsUsed ? (
                        <IconLock color={theme.colors['icon.primary']} size={30} strokeWidth={ICON_STROKE_WIDTH} />
                      ) : undefined
                    }
                    status={
                      freeBorrowIsUsed !== undefined ? (
                        <Text
                          color={freeBorrowIsUsed ? 'text.error' : 'text.positive'}
                          fontSize={14}
                          fontWeight="500"
                          lineHeight={20}
                        >
                          {creditUsedAt
                            ? t('design.usedOn', { date: localeAwareFormat(creditUsedAt, 'EEE d MMMM') })
                            : t('design.available')}
                        </Text>
                      ) : undefined
                    }
                  >
                    <Text
                      color="text.primary"
                      fontSize={16}
                      fontWeight="700"
                      textDecorationLine={freeBorrowIsUsed ? 'line-through' : 'none'}
                    >
                      {t('design.oneBorrow')}{' '}
                      <Text
                        color={freeBorrowIsUsed === false ? 'text.emphasized' : 'text.primary'}
                        fontSize={16}
                        fontWeight="700"
                      >
                        {t('design.free')}
                      </Text>
                    </Text>
                  </CheckFeature>
                </Column>
              </Column>

              <Button onPress={onActivate} variant="secondary">
                {t('design.useActivationCode')}
              </Button>

              {isLoanDiscountReady ? (
                <Column gap="spacing.general.x4">
                  <Text color="text.primary" fontSize={20} fontWeight="700">
                    {t('design.goFurther')}
                  </Text>
                  <Column borderColor="border.secondary" borderRadius="radius.lg" borderWidth={1} overflow="hidden">
                    <Row
                      alignItems="center"
                      backgroundColor="surface.brand.secondary-5.subtle"
                      gap="spacing.general.x2"
                      padding="spacing.general.x6"
                    >
                      <PartagePlusBadge />
                      {hasActiveLoanDiscount ? (
                        <Row
                          backgroundColor="surface.default"
                          borderRadius="radius.full"
                          paddingHorizontal="spacing.general.x4"
                          paddingVertical="spacing.general.x1"
                        >
                          <Text color="text.primary" fontSize={12} fontWeight="700" lineHeight={22}>
                            {t('loanDiscount.percentage', { ns: 'payment' })}
                          </Text>
                        </Row>
                      ) : null}
                    </Row>
                    <Column gap="spacing.general.x2" padding="spacing.general.x6">
                      <CheckFeature>{t('design.unlimitedLending')}</CheckFeature>
                      <FeatureDivider />
                      <CheckFeature>{t('design.unlimitedBorrowing')}</CheckFeature>
                      <FeatureDivider />
                      <DamageCoverageFeature onPress={showInsuranceDetails} showLearnMore />
                      <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x1">
                        {hasActiveLoanDiscount ? (
                          <Text color="text.secondary" fontSize={14} lineHeight={20} textDecorationLine="line-through">
                            {t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' })}/
                            {t(`period.${PlanRecurrenceEnum.yearly}`, { ns: 'payment' })}
                          </Text>
                        ) : null}
                        <Text
                          color={hasActiveLoanDiscount ? 'text.emphasized' : 'text.primary'}
                          fontSize={14}
                          fontWeight="700"
                          lineHeight={20}
                        >
                          {hasActiveLoanDiscount
                            ? t(`loanDiscount.price.${userCurrency}`, { ns: 'payment' })
                            : t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' })}{' '}
                          / {t(`period.${PlanRecurrenceEnum.yearly}`, { ns: 'payment' })}.
                        </Text>
                        <Text color="text.secondary" fontSize={14} lineHeight={20}>
                          {t('design.cancelAnytimeShort')}
                        </Text>
                      </Row>
                      <Button onPress={onSubscribe} variant="primary">
                        <Text color="text.inverse.primary">{t('design.subscribeNow')} →</Text>
                      </Button>
                      {hasActiveLoanDiscount ? (
                        <Text color="text.secondary" fontSize={10} lineHeight={12}>
                          {t('loanDiscount.footnote', {
                            ns: 'payment',
                            discountedPrice: t(`loanDiscount.price.${userCurrency}`, { ns: 'payment' }),
                            renewalPrice: t(`price.${PlanRecurrenceEnum.yearly}.${userCurrency}`, { ns: 'payment' }),
                          })}
                        </Text>
                      ) : null}
                    </Column>
                  </Column>
                </Column>
              ) : null}
            </Column>
          )}
        </Column>
      </Page>

      {isActivationModalOpen ? renderActivationModal : null}
      <Modal onClose={onCloseStopRecurrenceModal} open={isStopRecurrenceModalOpen} transparent>
        <Modal.Card>
          <Modal.Title>{t('stopRecurrenceModal.title')}</Modal.Title>
          <Column>
            <Text textAlign="center">
              {activePlan?.expiresAt
                ? t('stopRecurrenceModal.text', {
                    date: localeAwareFormat(
                      new Date(activePlan.expiresAt),
                      t('dateFormat_MMMMdYYYY', { ns: 'common' }),
                    ),
                  })
                : t('noEnd')}
            </Text>
          </Column>
          <Button loading={isStoppingRecurrence} onPress={onConfirmStopRecurrence} variant="primary">
            {t('stopRecurrenceModal.ctaPositive')}
          </Button>
          <Button onPress={onCloseStopRecurrenceModal} variant="secondary">
            {t('stopRecurrenceModal.ctaNegative')}
          </Button>
        </Modal.Card>
      </Modal>
    </>
  );
}
